// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

import { qs, qsAll } from '../utils/domHelpers.js';
import { stateManager } from '../utils/stateManager.js';
import { trapFocus, releaseFocus } from '../utils/focusManager.js';

export class Header {
  constructor(baseQueryEl) {
    this.baseQueryEl = baseQueryEl;
    this._outsideHandlerRef = null;
    this._outsideHandlerBound = false;
  }

  init() {
    this.initMenuToggle();
    this.initSearch();
    this.initCart();
    this.bindClickOutsideClosers();
  }

  initMenuToggle() {
		const menuToggle = qs(".menu__toggle", this.baseQueryEl);
		if (menuToggle) {
			menuToggle.setAttribute("tabindex", "0");
			menuToggle.setAttribute("aria-expanded", "false");

			const toggleMenu = () => {
				document.body.classList.remove(
					"search--previewed",
					"cart--previewed"
				);
				if (stateManager.getState() !== "menu") {
					stateManager.setState("menu");
					document.body.classList.add("menu--open");
					menuToggle.setAttribute("aria-expanded", "true");
					trapFocus([
						document.querySelector("header.page__header"),
						document.querySelector(".header__menu"),
					]);
				} else {
					stateManager.setState(null);
					document.body.classList.remove("menu--open");
					menuToggle.setAttribute("aria-expanded", "false");
					releaseFocus();
				}
			};

			menuToggle.addEventListener("click", toggleMenu);
			menuToggle.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					toggleMenu();
				}
			});
		}

		// Delegate event for closing the menu .header__menu-close
		const menuCloseButton = qs(".header__menu-close", this.baseQueryEl);
		if (menuCloseButton) {
			menuCloseButton.addEventListener("click", () => {
				stateManager.setState(null);
				document.body.classList.remove("menu--open");
				menuToggle.setAttribute("aria-expanded", "false");
				releaseFocus();
			});
		}
  }

  initSearch() {
    const searchButton = qs('.header__icon-search', this.baseQueryEl);
    const searchCloseButton = qs('.menu__toggle--search-close', this.baseQueryEl);
    const searchInput = qs('#modal__search_input', this.baseQueryEl);

    if (searchButton) {
      searchButton.setAttribute('tabindex', '0');
      searchButton.setAttribute('aria-expanded', 'false');
      searchButton.addEventListener('click', () => {
        document.body.classList.remove('menu--open', 'cart--previewed');
        if (stateManager.getState() !== 'search') {
          searchInput.focus();
          stateManager.setState('search');
          document.body.classList.add('search--previewed');
          searchButton.setAttribute('aria-expanded', 'true');
          if (searchInput) {
            setTimeout(function () {
              searchInput.focus();
            }, 300);
          }
          trapFocus([
				document.querySelector("header.page__header"),
				document.querySelector(".section-header"),
			]);
        } else {
          searchInput.blur();
          stateManager.setState(null);
          document.body.classList.remove('search--previewed');
          searchButton.setAttribute('aria-expanded', 'false');
          releaseFocus();
        }
      });
      searchButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          searchButton.click();
        }
      });
    }

    if (searchInput) {
      searchInput.addEventListener(
        'input',
        debounce(() => {
          if (searchInput.value.length >= 14) {
            searchInput.classList.add('length--extended');
          } else {
            searchInput.classList.remove('length--extended');
          }
        }, 150)
      );
    }

    if (searchCloseButton) {
      searchCloseButton.addEventListener('click', () => {
        if (stateManager.getState() === 'search') {
          searchInput.blur();
          stateManager.setState(null);
          document.body.classList.remove('search--previewed');
          releaseFocus();
        }
      });
    }
  }

  initCart() {
    const cartButton = qs('.header__cart', this.baseQueryEl);
    if (cartButton) {
      cartButton.setAttribute('tabindex', '0');
      cartButton.setAttribute('aria-expanded', 'false');
      cartButton.addEventListener('click', (e) => {
        e.preventDefault();
        document.body.classList.remove('menu--open', 'search--previewed');
        if (stateManager.getState() !== 'cart') {
          stateManager.setState('cart');
          document.body.classList.add('cart--previewed');
          cartButton.setAttribute('aria-expanded', 'true');
          setTimeout(function () {
            trapFocus([
				document.querySelector(".drawer__inner--cart"),
				document.querySelector("header.page__header"),
			]);
          }, 500);
          // trapFocus([document.querySelector('.drawer__inner--cart')]);
        } else {
          stateManager.setState(null);
          document.body.classList.remove('cart--previewed');
          cartButton.setAttribute('aria-expanded', 'false');
          releaseFocus();
        }
      });
      cartButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          cartButton.click();
        }
      });
    }

    // Delegate event for closing the cart.
    document.body.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.header__cart--close');
      if (closeBtn && stateManager.getState() === 'cart') {
        stateManager.setState(null);
        document.body.classList.remove('cart--previewed');
        releaseFocus();
      }
    });
  }

  bindClickOutsideClosers() {
    if (this._outsideHandlerBound) return;

    // The pointerdown that closes the menu/drawer is followed by a native
    // click on whatever was under the cursor (e.g. a product link). Without
    // this, that click still lands on the page content and activates it.
    // Swallow exactly that one click, then stop listening.
    let swallowTimeout = null;
    const swallowNextClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      document.removeEventListener('click', swallowNextClick, { capture: true });
      clearTimeout(swallowTimeout);
    };

    this._outsideHandlerRef = (event) => {
      const body = document.body;
      const t = event.target;
      let closedSomething = false;

      // If the menu is open, close it when clicking outside the header
      if (body.classList.contains('menu--open')) {
        const insideHeader = t.closest('.page__header');
        if (!insideHeader) {
          if (stateManager.getState() === 'menu') {
            stateManager.setState(null);
          }
          body.classList.remove('menu--open');
          releaseFocus();
          closedSomething = true;
        }
      }

      // If the cart drawer is previewed, close it when clicking outside the drawer
      if (body.classList.contains('cart--previewed')) {
        const insideCart = t.closest('.cart-drawer, .drawer');
        if (!insideCart) {
          if (stateManager.getState() === 'cart') {
            stateManager.setState(null);
          }
          body.classList.remove('cart--previewed');
          releaseFocus();
          const cartBtn = qs('.header__cart', this.baseQueryEl);
          if (cartBtn) cartBtn.setAttribute('aria-expanded', 'false');
          closedSomething = true;
        }
      }

      if (closedSomething) {
        event.preventDefault();
        document.addEventListener('click', swallowNextClick, { capture: true });
        // Fallback cleanup in case the pointer interaction never produces a
        // click (e.g. a drag/scroll), so we don't swallow an unrelated one.
        clearTimeout(swallowTimeout);
        swallowTimeout = setTimeout(() => {
          document.removeEventListener('click', swallowNextClick, { capture: true });
        }, 400);
      }
    };

    // Use capture so we get this before any stopPropagation inside UI controls
    document.addEventListener('pointerdown', this._outsideHandlerRef, { capture: true });
    this._outsideHandlerBound = true;
  }

  unbindClickOutsideClosers() {
    if (!this._outsideHandlerBound || !this._outsideHandlerRef) return;
    document.removeEventListener('pointerdown', this._outsideHandlerRef, { capture: true });
    this._outsideHandlerBound = false;
    this._outsideHandlerRef = null;
  }
}
